// Ferry-specific tuning for the shared entity-interpolation tracker.
//
// The NYC Ferry GTFS-rt feed reports vehicle positions every ~45s. Previously the
// ferry layer leaned on deck.gl's getPosition transitions, which interpolate by
// buffer index — so when the set of ferries changed between polls (boats dock and
// undock constantly), a marker would tween toward *another* ferry's slot, drawing a
// straight line clear across the harbor and over land. Tracking by ferry id instead
// (via the shared glide tracker) keeps every marker gliding toward its own next
// position, and a glide window kept just over the poll cadence means ferries are
// always mid-glide and never freeze between polls.
//
// Ferries cruise ~12 m/s (~25 kn) — much faster than buses — so their motion is
// actually perceptible at neighborhood zoom; the snap threshold sits well above a
// normal ~45s run (~600 m) but below a route-reassignment jump across the harbor.

export const FERRY_GLIDE_MS = 48_000; // just over the ~45s poll cadence: real-speed, rarely stalls
export const FERRY_SNAP_METERS = 3_000; // jumps beyond this teleport (route reassign / GPS reacquire)
export const FERRY_JITTER_METERS = 8; // ignore dock drift / GPS noise so moored ferries sit still

// Underway-vs-docked classification. The GTFS-rt feed reports each boat's actual
// `speed` (m/s) every snapshot — an INSTANT signal that's correct from the very
// first frame, unlike inferring motion from position history (which needs two polls
// and so left every boat looking docked/frozen for a full poll cycle on load).
// Docked boats report 0; cruising boats ~8–12 m/s. Anything clearly above drift is
// underway → bright + wake; at/near zero is docked → dim + small.
export const FERRY_MOVING_MPS = 0.5;

// Fallback when the feed omits speed: a ferry whose last poll moved it more than
// this is underway. ~0.7 m/s over a 45s poll — above GPS noise, below a cruising
// boat (~12 m/s ⇒ ~540 m/poll).
export const FERRY_MOVING_METERS = 30;

// Cap for closing on a revised path position. Boats cruise ~4–12 m/s; the LIRR
// default (20 m/s) looks like a speedboat lunge on water, so revisions absorb
// at most a little under cruise speed.
export const FERRY_CATCHUP_MPS = 8;

// Wake: a short fading trail along each underway ferry's recent track, built from
// the active glide (from → to). It covers the last FERRY_WAKE_MS of travel, so
// faster boats leave a longer wake — drawn as a few segments whose alpha ramps up
// toward the boat. Purely a motion cue; the track is the straight-line glide chord.
export const FERRY_WAKE_MS = 14_000; // trail length in travel-time behind the boat
export const FERRY_WAKE_SEGMENTS = 4; // sub-segments to fake an alpha gradient
export const FERRY_WAKE_ALPHA_NEAR = 150; // alpha at the boat (stern)
export const FERRY_WAKE_ALPHA_FAR = 16; // alpha at the far tail
